import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  BsCalendar2Check,
  BsChevronLeft,
  BsChevronRight,
  BsCalendar2Heart,
} from "react-icons/bs";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../../shared/libs/firebaseConfig";

import "../../../shared/styles/CardSlideForm.css";
import "./CourseCalendarSlide.css";

function toDateSafe(v) {
  try {
    if (v?.toDate) return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "number") return new Date(v);
    if (typeof v === "string") return new Date(v);
  } catch {}
  return null;
}

function courseHasCategory(course, category) {
  if (!category || category === "all") return true;
  const cats = Array.isArray(course?.categories) ? course.categories : [];
  return cats.includes(category);
}

function getCategoryLabel(selectedCategory) {
  if (!selectedCategory || selectedCategory === "all") return "";
  return selectedCategory;
}

export default function CourseScheduleSlide({
  selectedCategory = "all",
  maxFetch = 60,
  titlePrefix = "ตารางเรียน",
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [coursesBase, setCoursesBase] = useState([]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);

  const slideRef = useRef(null);
  const initialScrollRef = useRef(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  const categoryLabel = getCategoryLabel(selectedCategory);

  useEffect(() => {
    let alive = true;

    async function loadCourses() {
      try {
        setLoading(true);
        setError("");

        const q = query(
          collection(db, "courses"),
          where("isActive", "==", true),
          orderBy("updatedAt", "desc"),
          limit(maxFetch)
        );

        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        if (!alive) return;
        setCoursesBase(rows);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setError(e?.message || "โหลดรูปปฏิทินไม่สำเร็จ");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadCourses();

    return () => {
      alive = false;
    };
  }, [maxFetch]);

  const scheduleImages = useMemo(() => {
    const courses = [...coursesBase]
        .filter((course) => courseHasCategory(course, selectedCategory))
        .filter((course) => course?.showCourseScheduleImages !== false)
        .sort((a, b) => {
        const da = toDateSafe(a.updatedAt) || toDateSafe(a.createdAt) || new Date(0);
        const dbb = toDateSafe(b.updatedAt) || toDateSafe(b.createdAt) || new Date(0);
        return dbb.getTime() - da.getTime();
        });

    return courses.flatMap((course) => {
        const imgs = Array.isArray(course?.courseScheduleImages)
        ? course.courseScheduleImages
        : [];

        return imgs
        .filter((img) => img?.imageUrl && img?.isActive !== false)
        .map((img, index) => ({
            id: `${course.id}-${img.id || index}`,
            imageUrl: img.imageUrl,
            name: img.name || course.title || "ตารางเรียน",
            courseId: course.id,
            courseTitle: course.title || "",
            categories: Array.isArray(course.categories) ? course.categories : [],
        }));
    });
    }, [coursesBase, selectedCategory]);

  useEffect(() => {
    const el = slideRef.current;
    if (!el) return;

    el.scrollLeft = 0;
    initialScrollRef.current = null;
    setHasInteracted(false);

    const updateArrows = () => {
      const now = slideRef.current;
      if (!now) return;

      const { scrollLeft, scrollWidth, clientWidth } = now;
      if (initialScrollRef.current === null) {
        initialScrollRef.current = scrollLeft;
      }

      if (scrollWidth <= clientWidth + 1) {
        setCanScrollLeft(false);
        setCanScrollRight(false);
        return;
      }

      const base = initialScrollRef.current ?? 0;
      const atStart = scrollLeft <= base + 1;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      setCanScrollLeft(!atStart);
      setCanScrollRight(!atEnd);
    };

    updateArrows();
    el.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);

    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [scheduleImages.length, selectedCategory]);

  const handleSlideNext = () => {
    const el = slideRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth * 0.8, behavior: "smooth" });
    setHasInteracted(true);
  };

  const handleSlidePrev = () => {
    const el = slideRef.current;
    if (!el) return;
    el.scrollBy({ left: -el.clientWidth * 0.8, behavior: "smooth" });
    setHasInteracted(true);
  };

  const openPreview = (index) => {
    setPreviewImages(scheduleImages);
    setPreviewIndex(index);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewImages([]);
    setPreviewIndex(0);
  };

  const activePreview = previewImages[previewIndex];

  if (!loading && !error && scheduleImages.length === 0) {
    return null;
  }

  return (
    <>
      <div className="course-calendar-section">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h3 className="mb-0 d-flex align-items-center gap-2">
            <BsCalendar2Heart />
                <strong>
                    {titlePrefix}
                    {categoryLabel ? ` "${categoryLabel}"` : ""}
                </strong>
          </h3>
        </div>

        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        )}

        {error && <div className="text-danger my-3">{error}</div>}

        {!loading && !error && scheduleImages.length > 0 && (
          <div
            className={`slide-wrapper course-calendar-slide-wrapper ${
              canScrollLeft || canScrollRight ? "has-scroll" : "no-scroll"
            }`}
          >
            {hasInteracted && canScrollLeft && (
              <button
                type="button"
                className="slide-arrow slide-arrow-left"
                onClick={handleSlidePrev}
                aria-label="เลื่อนซ้าย"
              >
                <BsChevronLeft size={34} />
              </button>
            )}

            <div
              className="course-calendar-slide-container schedule-slide-container"
              ref={slideRef}
              onTouchStart={() => setHasInteracted(true)}
              onMouseDown={() => setHasInteracted(true)}
            >
              {scheduleImages.map((img, index) => (
                <button
                  key={img.id}
                  type="button"
                  className="course-calendar-slide-item schedule-slide-item"
                  onClick={() => openPreview(index)}
                >
                  <img
                    src={img.imageUrl}
                    alt={img.name || "ตารางปฏิทินเรียน"}
                    loading="lazy"
                  />
                </button>
              ))}
            </div>

            {canScrollRight && (
              <button
                type="button"
                className="slide-arrow slide-arrow-right"
                onClick={handleSlideNext}
                aria-label="เลื่อนขวา"
              >
                <BsChevronRight size={34} />
              </button>
            )}
          </div>
        )}
      </div>

      {previewOpen ? (
        <div className="course-calendar-lightbox" onClick={closePreview}>
          <div
            className="course-calendar-lightbox-inner"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="course-calendar-lightbox-close"
              onClick={closePreview}
            >
              ×
            </button>

            {activePreview?.imageUrl ? (
              <div className="course-calendar-main-image-wrap">
                <img
                  src={activePreview.imageUrl}
                  alt={activePreview.name || "ตารางปฏิทินเรียน"}
                  className="course-calendar-main-image"
                />
              </div>
            ) : null}

            <div className="course-calendar-thumb-row">
              {previewImages.map((img, index) => (
                <button
                  key={img.id}
                  type="button"
                  className={`course-calendar-thumb ${
                    index === previewIndex ? "is-active" : ""
                  }`}
                  onClick={() => setPreviewIndex(index)}
                >
                  <img src={img.imageUrl} alt={img.name || "thumbnail"} />
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}